/**
 * Devis : traitement du texte, sans dépendance au PDF ni au serveur
 * (testable seul).
 *
 *  - normalisation pour la recherche (minuscules, sans accents, traits
 *    d'union remplacés par des espaces), caractère pour caractère : le texte
 *    normalisé a exactement la même longueur que l'original, ce qui permet
 *    de surligner les résultats dans le texte d'origine ;
 *  - lecture des termes cherchés ;
 *  - extraits avec surlignage ;
 *  - détection de la section MasterFormat de chaque page (« 07 21 00 »).
 */

// ---------------------------------------------------------------------
// Normalisation
// ---------------------------------------------------------------------

const TIRETS = new Set(['-', '‐', '‑', '‒', '–', '—', '―', '−'])
const APOSTROPHES = new Set(['’', '‘', 'ʼ', '`', '´'])

/** Minuscules, sans accents ; tirets et blancs → espace. Même longueur. */
export function normaliser(s: string): string {
  let r = ''
  for (let i = 0; i < s.length; i++) {
    const c = s[i]
    if (c === ' ' || c === '\n' || c === '\t' || c === '\r' || c === ' ' || c === ' ' || TIRETS.has(c)) {
      r += ' '
    } else if (APOSTROPHES.has(c)) {
      r += "'"
    } else {
      const d = c.toLowerCase().normalize('NFD')[0]
      r += d !== undefined && d.length === 1 ? d : c
    }
  }
  return r
}

const LIGATURES: Record<string, string> = {
  'ﬀ': 'ff',
  'ﬁ': 'fi',
  'ﬂ': 'fl',
  'ﬃ': 'ffi',
  'ﬄ': 'ffl',
  'ﬅ': 'st',
  'ﬆ': 'st'
}

/** Nettoie une ligne extraite du PDF : ligatures, césures invisibles, espaces. */
export function nettoyerLigne(s: string): string {
  return s
    .replace(/[ﬀ-ﬆ]/g, (c) => LIGATURES[c] ?? c)
    .replace(/­/g, '')
    .replace(/[ \t  ]+/g, ' ')
    .trim()
}

/** Assemble les lignes d'une page en un texte (mot coupé par un tiret en fin de ligne : recollé). */
export function assemblerLignes(lignes: string[]): string {
  let texte = ''
  for (const brute of lignes) {
    const l = nettoyerLigne(brute)
    if (!l) continue
    if (!texte) texte = l
    else if (/\p{L}-$/u.test(texte) && /^\p{Ll}/u.test(l)) texte += l
    else texte += '\n' + l
  }
  return texte
}

// ---------------------------------------------------------------------
// Recherche
// ---------------------------------------------------------------------

/**
 * Termes d'une recherche. Les mots sont tous exigés (ET) ; une expression
 * entre guillemets est cherchée telle quelle. « pare-air » = « pare air ».
 */
export function termes(requete: string): string[] {
  const out: string[] = []
  const re = /"([^"]+)"|(\S+)/g
  let m: RegExpExecArray | null
  while ((m = re.exec(requete))) {
    let t = normaliser(m[1] ?? m[2]).replace(/[*%_\\]/g, ' ')
    if (m[2] !== undefined) t = t.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '')
    t = t.replace(/\s+/g, ' ').trim()
    if (t.length >= 2 && !out.includes(t)) out.push(t)
  }
  return out
}

export interface Morceau {
  t: string
  /** Vrai : partie trouvée (surlignée). */
  m: boolean
}

/** Positions des termes dans le texte (début, fin), triées. */
export function occurrences(texte: string, t: string[]): [number, number][] {
  const norm = normaliser(texte)
  const plages: [number, number][] = []
  for (const x of t) {
    let i = norm.indexOf(x)
    while (i >= 0) {
      plages.push([i, i + x.length])
      i = norm.indexOf(x, i + x.length)
    }
  }
  return plages.sort((a, b) => a[0] - b[0])
}

/** Jusqu'à « max » extraits autour des termes trouvés, avec les termes surlignés. */
export function extraits(texte: string, t: string[], max = 3, rayon = 70): Morceau[][] {
  const plages = occurrences(texte, t)
  if (plages.length === 0) {
    const debut = texte.slice(0, rayon * 2).replace(/\s+/g, ' ')
    return [[{ t: debut + (texte.length > rayon * 2 ? '…' : ''), m: false }]]
  }
  const fenetres: [number, number][] = []
  for (const [a, b] of plages) {
    const der = fenetres[fenetres.length - 1]
    if (der && a - rayon <= der[1]) der[1] = Math.max(der[1], b + rayon)
    else if (fenetres.length < max) fenetres.push([Math.max(0, a - rayon), b + rayon])
    else break
  }
  return fenetres.map(([d, f0]) => {
    const f = Math.min(f0, texte.length)
    const morceaux: Morceau[] = []
    if (d > 0) morceaux.push({ t: '…', m: false })
    let pos = d
    for (const [a, b] of plages) {
      if (b <= pos || a >= f) continue
      const aa = Math.max(a, pos)
      const bb = Math.min(b, f)
      if (aa > pos) morceaux.push({ t: texte.slice(pos, aa), m: false })
      morceaux.push({ t: texte.slice(aa, bb), m: true })
      pos = bb
    }
    if (pos < f) morceaux.push({ t: texte.slice(pos, f), m: false })
    if (f < texte.length) morceaux.push({ t: '…', m: false })
    return morceaux.map((x) => ({ ...x, t: x.t.replace(/\s+/g, ' ') }))
  })
}

// ---------------------------------------------------------------------
// Sections MasterFormat
// ---------------------------------------------------------------------

/** Divisions MasterFormat existantes : filtre les faux codes (dates, numéros). */
const DIVISIONS = new Set([
  '00', '01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12', '13', '14',
  '21', '22', '23', '25', '26', '27', '28', '31', '32', '33', '34', '35',
  '40', '41', '42', '43', '44', '46', '48'
])

export interface CodeTrouve {
  code: string
  debut: number
  fin: number
  /** Le mot « section » précède le code. */
  section: boolean
}

/** Codes de section dans une ligne : « 07 21 00 », « 07.21.00 », « Section 072100 », « Section 07210 ». */
export function codesDansLigne(ligne: string): CodeTrouve[] {
  const out: CodeTrouve[] = []
  const re = /(^|[^\d.,/])(\d{2})([ .]?)(\d{2})\3(\d{2})(?:\.\d{1,2})?(?![\d,/])/g
  let m: RegExpExecArray | null
  while ((m = re.exec(ligne))) {
    const debut = m.index + m[1].length
    const avant = ligne.slice(Math.max(0, debut - 14), debut)
    const section = /section\s*(n[o°]\.?\s*)?:?\s*$/i.test(avant)
    if (!DIVISIONS.has(m[2])) continue
    // Six chiffres collés : seulement après le mot « section ».
    if (m[3] === '' && !section) continue
    out.push({ code: `${m[2]} ${m[4]} ${m[5]}`, debut, fin: m.index + m[0].length, section })
  }
  const ancien = /section\s*(n[o°]\.?\s*)?:?\s*(\d{5})(?![\d])/gi
  while ((m = ancien.exec(ligne))) {
    if (!DIVISIONS.has(m[2].slice(0, 2))) continue
    const debut = m.index + m[0].length - 5
    if (out.some((c) => c.debut <= debut && c.fin > debut)) continue
    out.push({ code: m[2], debut, fin: debut + 5, section: true })
  }
  return out.sort((a, b) => a.debut - b.debut)
}

/** Titre de section tiré d'une ligne, une fois le code, « section » et « page x de y » retirés. */
export function titreDansLigne(ligne: string, c: CodeTrouve): string | null {
  const nettoyer = (s: string) =>
    s
      .replace(/\bsection\s*(n[o°]\.?\s*)?:?\s*$/i, '')
      .replace(/\bpage\s*\d+\s*(de|of|sur|\/)\s*\d+/gi, ' ')
      .replace(/\b\d+\s*(de|sur|\/)\s*\d+\s*$/i, ' ')
      .replace(/\bpage\s*\d+\b/gi, ' ')
      .replace(/^[\s\-–—:|.,]+|[\s\-–—:|.,]+$/g, '')
      .replace(/\s+/g, ' ')
      .trim()
  const apres = nettoyer(ligne.slice(c.fin))
  const avant = nettoyer(ligne.slice(0, c.debut))
  for (const t of [apres, avant]) {
    if ((t.match(/\p{L}/gu) ?? []).length >= 3 && t.length <= 90) return t
  }
  return null
}

/** Une ligne de texte d'une page, avec sa position verticale (0 = haut, 1 = bas). */
export interface LignePage {
  y: number
  texte: string
}

export interface SectionPage {
  code: string
  titre: string | null
}

/**
 * Section d'une page, d'après son en-tête ou son pied de page
 * (« Section 07 21 00 — Isolation thermique — Page 3 de 8 »), ou d'après
 * un début de section en haut de page (« SECTION 07 21 00 » puis le titre).
 * Les mentions dans le corps du texte (« voir section 07 92 00 ») sont ignorées.
 */
export function sectionDePage(lignes: LignePage[]): SectionPage | null {
  interface Candidat {
    score: number
    zone: boolean
    code: string
    titre: string | null
  }
  let meilleur: Candidat | null = null
  const codesCorps = new Set<string>()

  lignes.forEach((l, i) => {
    const zone = l.y < 0.12 || l.y > 0.88
    for (const c of codesDansLigne(l.texte)) {
      const avantCode = l.texte.slice(0, c.debut).replace(/\bsection\s*(n[o°]\.?\s*)?:?\s*$/i, '').trim()
      const debutLigne = avantCode === ''
      if (!zone) codesCorps.add(c.code)
      // Dans le corps : seulement un début de section en haut de page
      // (« SECTION 07 21 00 », seul sur sa ligne ou suivi du titre).
      const debutSection = !zone && l.y < 0.35 && debutLigne && (c.section || l.texte.trim().length - c.fin < 4)
      if (!zone && !debutSection) continue
      let titre = titreDansLigne(l.texte, c)
      // « SECTION 07 27 00 » seul sur sa ligne, en haut : le titre est la ligne suivante.
      if (!titre && debutLigne && l.y < 0.5 && l.texte.trim().length - c.fin < 4) {
        const suivante = lignes[i + 1]?.texte.trim() ?? ''
        if ((suivante.match(/\p{L}/gu) ?? []).length >= 3 && suivante.length <= 90 && codesDansLigne(suivante).length === 0) {
          titre = suivante
        }
      }
      const score = (c.section ? 3 : 0) + (zone ? 2 : 0) + (debutLigne ? 1 : 0) + (titre ? 0.5 : 0)
      if (!meilleur || score > meilleur.score) meilleur = { score, zone, code: c.code, titre }
    }
  })

  const m = meilleur as Candidat | null
  if (!m) return null
  // Table des matières : plusieurs codes dans le corps de la page. Un
  // « début de section » y serait trompeur ; seul l'en-tête ou le pied compte.
  if (codesCorps.size >= 3 && !m.zone) return null
  return { code: m.code, titre: m.titre }
}

/** Code de section dans un nom de fichier (« 07 21 00 - Isolation.pdf »). */
export function sectionDuNomFichier(nom: string): SectionPage | null {
  const base = nom.replace(/\.pdf$/i, '').replace(/_/g, ' ')
  const c = codesDansLigne(base)[0] ?? codesDansLigne('section ' + base.replace(/^\D+/, ''))[0]
  if (!c) return null
  return { code: c.code, titre: titreDansLigne(base, c) }
}

/**
 * Sections de toutes les pages d'un document : chaque page sans section
 * trouvée hérite de la précédente ; le titre retenu pour un code est celui
 * qui revient le plus souvent.
 */
export function sectionsDuDocument(parPage: (SectionPage | null)[], nomFichier: string): (SectionPage | null)[] {
  const out: (SectionPage | null)[] = []
  let courante: SectionPage | null = null
  for (const s of parPage) {
    if (s) courante = s
    out.push(courante ? { ...courante } : null)
  }
  if (!out.some(Boolean)) {
    const f = sectionDuNomFichier(nomFichier)
    if (f) return parPage.map(() => ({ ...f }))
  }
  const votes = new Map<string, Map<string, number>>()
  for (const s of parPage) {
    if (!s?.titre) continue
    const v = votes.get(s.code) ?? new Map<string, number>()
    v.set(s.titre, (v.get(s.titre) ?? 0) + 1)
    votes.set(s.code, v)
  }
  return out.map((s) => {
    if (!s) return null
    const v = votes.get(s.code)
    if (!v) return s
    const [titre] = [...v.entries()].sort((a, b) => b[1] - a[1])[0]
    return { code: s.code, titre }
  })
}
